import type { DemoScript } from '../../../types/demo';

const AUDIO_BASE = 'https://bigsplice-demo-audio.s3.amazonaws.com/sheets-india';
const AUDIO_VERSION = 'v2';

/** Mock file list for demo sidebar */
const DEMO_FILES = [
  { fileName: 'India', fileSize: 2_400_000_000, uploadDate: '2026-02-20T08:00:00Z', s3Key: 'users/demo/data/India/data.parquet', isMultipart: true },
  { fileName: 'quarterly-sales', fileSize: 2_450_000, uploadDate: '2026-02-18T10:00:00Z', s3Key: 'users/demo/data/quarterly-sales/data.parquet' },
  { fileName: 'call-transcripts', fileSize: 4_200_000, uploadDate: '2026-02-15T12:00:00Z', s3Key: 'users/demo/data/call-transcripts/data.parquet' },
];

/** The India file reference */
const INDIA_FILE = DEMO_FILES[0];

/** Mock schema for India demographics dataset (8 columns) */
const DEMO_SCHEMA = [
  { name: 'id', data_type: 'INT64' },
  { name: 'first_name', data_type: 'BYTE_ARRAY' },
  { name: 'last_name', data_type: 'BYTE_ARRAY' },
  { name: 'email', data_type: 'BYTE_ARRAY' },
  { name: 'phone', data_type: 'BYTE_ARRAY' },
  { name: 'city', data_type: 'BYTE_ARRAY' },
  { name: 'state', data_type: 'BYTE_ARRAY' },
  { name: 'company', data_type: 'BYTE_ARRAY' },
];

const DEMO_TOTAL_ROWS = 17_142_831;

/** 6 sample rows of mock Indian demographics data */
const DEMO_ROWS = [
  { id: 1, first_name: 'Abhishek', last_name: 'Sharma', email: 'abhishek.sharma@example.in', phone: '+91 98765 43210', city: 'Mumbai', state: 'Maharashtra', company: 'Tata Consultancy Services' },
  { id: 2, first_name: 'Priya', last_name: 'Patel', email: 'priya.patel@example.in', phone: '+91 87654 32109', city: 'Ahmedabad', state: 'Gujarat', company: 'Infosys Limited' },
  { id: 3, first_name: 'Rajesh', last_name: 'Kumar', email: 'rajesh.kumar@example.in', phone: '+91 76543 21098', city: 'Delhi', state: 'Delhi', company: 'Wipro Technologies' },
  { id: 4, first_name: 'Sneha', last_name: 'Reddy', email: 'sneha.reddy@example.in', phone: '+91 65432 10987', city: 'Hyderabad', state: 'Telangana', company: 'HCL Technologies' },
  { id: 5, first_name: 'Vikram', last_name: 'Singh', email: 'vikram.singh@example.in', phone: '+91 54321 09876', city: 'Jaipur', state: 'Rajasthan', company: 'Tech Mahindra' },
  { id: 6, first_name: 'Ananya', last_name: 'Gupta', email: 'ananya.gupta@example.in', phone: '+91 43210 98765', city: 'Bangalore', state: 'Karnataka', company: 'Mindtree Ltd' },
];

/** Mock search results for "abhishek" query */
const DEMO_SEARCH_RESULTS = [
  { id: 1, first_name: 'Abhishek', last_name: 'Sharma', email: 'abhishek.sharma@example.in', city: 'Mumbai', state: 'Maharashtra', company: 'Tata Consultancy Services' },
  { id: 847, first_name: 'Abhishek', last_name: 'Verma', email: 'abhishek.verma@example.in', city: 'Lucknow', state: 'Uttar Pradesh', company: 'Cognizant Technology' },
  { id: 2391, first_name: 'Abhishek', last_name: 'Joshi', email: 'abhishek.joshi@example.in', city: 'Pune', state: 'Maharashtra', company: 'Persistent Systems' },
  { id: 15280, first_name: 'Abhishek', last_name: 'Nair', email: 'abhishek.nair@example.in', city: 'Chennai', state: 'Tamil Nadu', company: 'Zoho Corporation' },
];

/**
 * Sheets page demo script — India dataset walkthrough.
 * Showcases GPU-accelerated grid browsing and full-text search
 * across 17 million rows of demographic data.
 */
export const sheetsIndiaScript: DemoScript = {
  id: 'sheets-india',
  templateId: 'sheets-india',
  name: 'BigSplice Sheets Demo',
  description: 'Explore 17 million rows of data with GPU-accelerated browsing and full-text search.',
  steps: [
    // === INTRO ===
    {
      id: 'intro',
      type: 'narrate',
      delayBefore: 300,
      subtitle: "Welcome to BigSplice Sheets. I'm going to show you how to explore massive datasets \u2014 we're talking 17 million rows \u2014 right in your browser. Let me walk you through it.",
      audio: { src: `${AUDIO_BASE}/intro.mp3?${AUDIO_VERSION}`, duration: 8000 },
    },

    // Inject mock files into sidebar (before narration so files are visible)
    {
      id: 'inject-files',
      type: 'inject',
      inject: {
        nodeId: '__sheets_page',
        data: {
          demoPhase: 'files-loaded',
          files: DEMO_FILES,
        },
      },
      autoAdvanceAfter: 300,
    },

    // === FILE SIDEBAR ===
    {
      id: 'sidebar-highlight',
      type: 'highlight',
      target: { selector: '[data-demo="file-sidebar"]' },
      delayBefore: 200,
      subtitle: "This sidebar shows all your uploaded datasets. Files are organized by folder \u2014 you can have CSVs, Parquet files, even multi-gigabyte datasets.",
      audio: { src: `${AUDIO_BASE}/sidebar-highlight.mp3?${AUDIO_VERSION}`, duration: 7000 },
      cursorPath: [
        { x: 0, y: -230, duration: 500 },
        { x: 0, y: -170, duration: 500 },
        { x: 0, y: -110, duration: 400 },
      ],
    },

    // === SELECT INDIA FILE ===
    {
      id: 'india-highlight',
      type: 'highlight',
      target: { selector: '[data-demo="file-India"]' },
      subtitle: "Let's open the India dataset. Watch how fast it loads.",
      audio: { src: `${AUDIO_BASE}/india-highlight.mp3?${AUDIO_VERSION}`, duration: 3500 },
      cursorPath: [
        { x: 0, y: -20, duration: 400 },
        { x: 0, y: 0, duration: 400 },
      ],
    },

    // Inject: select India file + load schema + initial rows
    {
      id: 'inject-select-india',
      type: 'inject',
      inject: {
        nodeId: '__sheets_page',
        data: {
          demoPhase: 'file-selected',
          selectedFile: INDIA_FILE,
          schema: DEMO_SCHEMA,
          totalRows: DEMO_TOTAL_ROWS,
          rows: DEMO_ROWS,
        },
      },
      subtitle: 'Loading data\u2026',
      autoAdvanceAfter: 800,
    },

    // === BROWSE DATA ===
    {
      id: 'grid-highlight',
      type: 'highlight',
      target: { selector: '[data-demo="canvas-grid"]' },
      delayBefore: 400,
      subtitle: "Here's your data \u2014 a GPU-accelerated grid that renders millions of rows without breaking a sweat. Each row is a person with name, email, phone, city, state, and company.",
      audio: { src: `${AUDIO_BASE}/grid-highlight.mp3?${AUDIO_VERSION}`, duration: 9000 },
      cursorPath: [
        { x: -100, y: -40, duration: 500 },
        { x: 0, y: 0, duration: 500 },
        { x: 80, y: 30, duration: 400 },
      ],
    },

    {
      id: 'stats-narrate',
      type: 'narrate',
      target: { selector: '[data-demo="stats-bar"]' },
      subtitle: "Notice the row count \u2014 17 million, 142 thousand rows across 8 columns. The grid uses virtual scrolling so you can jump anywhere instantly.",
      audio: { src: `${AUDIO_BASE}/stats-narrate.mp3?${AUDIO_VERSION}`, duration: 7000 },
    },

    {
      id: 'grid-decision',
      type: 'decision',
      target: { selector: '[data-demo="canvas-grid"]' },
      subtitle: '',
      decision: {
        prompt: 'Want to explore the data or jump to search?',
        options: [
          { label: 'Browse the grid', icon: 'search', nextStepId: 'grid-interact' },
          { label: 'Show me search', icon: 'arrow-right', nextStepId: 'search-highlight' },
        ],
        timeoutMs: 6000,
        defaultOptionIndex: 1,
      },
    },

    {
      id: 'grid-interact',
      type: 'interact',
      target: { selector: '[data-demo="canvas-grid"]' },
      subtitle: 'Take a moment to scroll through the data. Use mouse wheel, arrow keys, or Page Up/Down. You can also drag column headers to resize them.',
      audio: { src: `${AUDIO_BASE}/grid-interact.mp3?${AUDIO_VERSION}`, duration: 6000 },
      autoAdvanceAfter: 5000,
    },

    // === FULL-TEXT SEARCH ===
    {
      id: 'search-highlight',
      type: 'highlight',
      target: { selector: '[data-demo="search-toolbar"]' },
      subtitle: "Now for the powerful part \u2014 full-text search. BigSplice scans your entire dataset so you can find any term across all columns. No indexing required \u2014 it works on any data you upload, right away.",
      audio: { src: `${AUDIO_BASE}/search-highlight.mp3?${AUDIO_VERSION}`, duration: 15000 },
      cursorPath: [
        { x: -60, y: 0, duration: 400 },
        { x: 0, y: 0, duration: 500 },
      ],
    },

    {
      id: 'search-decision',
      type: 'decision',
      target: { selector: '[data-demo="search-toolbar"]' },
      subtitle: '',
      decision: {
        prompt: 'Want to watch a demo search or try it yourself?',
        options: [
          { label: 'Watch demo search', icon: 'play', nextStepId: 'search-demo-type' },
          { label: 'Try it myself', icon: 'keyboard', nextStepId: 'search-self' },
        ],
        timeoutMs: 5000,
        defaultOptionIndex: 0,
      },
    },

    // Demo search: type "abhishek"
    {
      id: 'search-demo-type',
      type: 'interact',
      target: { selector: '[data-demo="search-input"]' },
      subtitle: "Let me search for a name \u2014 watch this.",
      audio: { src: `${AUDIO_BASE}/search-typing.mp3?${AUDIO_VERSION}`, duration: 2500 },
      interaction: { action: 'type', delay: 120, value: 'abhishek' },
      autoAdvanceAfter: 1200,
    },

    // Inject search results
    {
      id: 'inject-search-results',
      type: 'inject',
      inject: {
        nodeId: '__sheets_page',
        data: {
          demoPhase: 'search-results',
          searchQuery: 'abhishek',
          searchResults: DEMO_SEARCH_RESULTS,
          totalMatched: 4,
          searchTime: 1100,
        },
      },
      autoAdvanceAfter: 800,
    },

    {
      id: 'search-results-narrate',
      type: 'narrate',
      target: { selector: '[data-demo="canvas-grid"]' },
      subtitle: "Four matches found in about a second \u2014 brute-force scanning every row. No indexing, no pre-processing. Upload new data and search it immediately.",
      audio: { src: `${AUDIO_BASE}/search-results.mp3?${AUDIO_VERSION}`, duration: 10000 },
    },

    // Free search time
    {
      id: 'search-self',
      type: 'interact',
      target: { selector: '[data-demo="search-input"]' },
      subtitle: "Go ahead and type any search term. Try a city name like 'Mumbai' or a company like 'Tata'.",
      audio: { src: `${AUDIO_BASE}/search-self.mp3?${AUDIO_VERSION}`, duration: 5000 },
      autoAdvanceAfter: 5000,
    },

    // === ENDING ===
    {
      id: 'ending',
      type: 'narrate',
      subtitle: "That's BigSplice Sheets \u2014 upload any dataset, browse millions of rows in a GPU-accelerated grid, and search across everything with no setup required. Ready to try it with your own data?",
      audio: { src: `${AUDIO_BASE}/ending.mp3?${AUDIO_VERSION}`, duration: 13000 },
    },

    {
      id: 'ending-decision',
      type: 'decision',
      subtitle: '',
      decision: {
        prompt: 'What would you like to do next?',
        options: [
          { label: 'Upload my own data', icon: 'upload', nextStepId: '__dismiss' },
          { label: 'Replay demo', icon: 'repeat', nextStepId: '__replay' },
          { label: 'Ask a question', icon: 'help-circle', nextStepId: '__ask' },
        ],
        timeoutMs: 0,
        defaultOptionIndex: -1,
      },
    },
  ],
};
